import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PawPrint, Star, Image as ImageIcon } from "lucide-react";
import { HotelManualInfo, useHotelManualInfo } from "@/hooks/useHotelManualInfo";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface HotelManualInfoSectionProps {
  manualInfo: HotelManualInfo;
}

export function HotelManualInfoSection({ manualInfo }: HotelManualInfoSectionProps) {
  const { t, i18n } = useTranslation();
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const { getLocalizedReviewText } = useHotelManualInfo(null);

  // Get localized review text
  const reviewText = getLocalizedReviewText(manualInfo);
  
  const hasReview = reviewText && reviewText.trim().length > 0;
  const hasPetInfo = manualInfo.is_pet_friendly !== null;
  const hasPhotos = manualInfo.photos && manualInfo.photos.length > 0;

  // Don't render if no useful info
  if (!hasReview && !hasPetInfo && !hasPhotos) {
    return null;
  }

  return (
    <>
      <Card className="border-2 border-amber-400/50 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-amber-200 dark:border-amber-800">
            <div className="p-2 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-md">
              <Star className="h-5 w-5 text-white fill-white" />
            </div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 dark:from-amber-400 dark:to-orange-400 bg-clip-text text-transparent">{t('hotelDetail.ourReview', 'Nuestra Evaluación')}</h2>
          </div>

          {/* Pet-Friendly Badge */}
          {hasPetInfo && (
            <div className="mb-4">
              {manualInfo.is_pet_friendly ? (
                <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 gap-1">
                  <PawPrint className="h-3 w-3" />
                  {t('hotelDetail.petFriendly', 'Pet-Friendly')}
                </Badge>
              ) : (
                <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 gap-1">
                  <PawPrint className="h-3 w-3" />
                  {t('hotelDetail.noPets', 'No acepta mascotas')}
                </Badge>
              )}
            </div>
          )}

          {/* Review Text */}
          {hasReview && (
            <div className="prose prose-sm dark:prose-invert max-w-none mb-4">
              <p className="text-foreground/90 whitespace-pre-line leading-relaxed">
                {reviewText}
              </p>
            </div>
          )}

          {/* Manual Photos */}
          {hasPhotos && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-3">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  {t('hotelDetail.ourPhotos', 'Nuestras Fotos')}
                </span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {manualInfo.photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedPhoto(photo)}
                    className="aspect-square rounded-lg overflow-hidden bg-muted hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    <img
                      src={photo}
                      alt={`${t('hotelDetail.photo', 'Foto')} ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photo Lightbox Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {selectedPhoto && (
            <img
              src={selectedPhoto}
              alt={t('hotelDetail.photo', 'Foto')}
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
